import { inject, Injectable, NgZone } from '@angular/core';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { BlueprintContext } from '../../drydock/orchestrator/blueprint.types';
import { SessionGuardService } from './session-guard.service';

export interface BlueprintUsageStep {
  model: string;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  totalTokens: number;
  costUsd: number;
}

export interface BlueprintUsage {
  steps: BlueprintUsageStep[];
  totals: {
    inputTokens: number;
    cachedInputTokens: number;
    outputTokens: number;
    thinkingTokens: number;
    totalTokens: number;
    costUsd: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class BlueprintService {
  private zone = inject(NgZone);
  private authService = inject(AuthService);
  private sessionGuard = inject(SessionGuardService);

  streamIterate(
    body: {
      clientId: string;
      pageName?: string;
      pageDescription?: string;
      originalScreenshot?: string;
      pageType?: 'web' | 'mobile';
      /**
       * The page registry, globals and data model. OMITTING IT DISABLES THE ENTIRE INTERACTIVITY
       * LAYER — the CF gates on `!!context` — and the system prompt still tells the model "the
       * registry below is COMPLETE", so an absent registry reads as "there are no pages" and every
       * cross-page control comes back `inert`. Build it with BlueprintContextBuilder; never omit it.
       */
      context?: BlueprintContext;
    },
    handlers: {
      onSession?: (sessionId: string) => void;
      onProgress?: (phase: string, message: string) => void;
      onResult?: (data: { pageJson?: any; pageId: string; pageName: string; sessionId: string }) => void;
      onUsage?: (data: BlueprintUsage) => void;
      onError?: (message: string) => void;
    },
    signal: AbortSignal,
  ): void {
    this.stream(
      `${environment.functionsBaseUrl}/blueprintIterate`,
      body,
      {
        session: (p) => handlers.onSession?.(p.sessionId),
        progress: (p) => handlers.onProgress?.(p.phase, p.message),
        result: (p) => handlers.onResult?.(p),
        usage: (p) => handlers.onUsage?.(p),
        error: (p) => handlers.onError?.(p.message),
      },
      signal,
    );
  }

  private async stream(
    url: string,
    body: object,
    handlers: Record<string, (parsed: any) => void>,
    signal: AbortSignal,
  ): Promise<void> {
    const token = await this.authService.getAuthToken();
    if (!token) {
      handlers['error']?.({ message: 'Not authenticated' });
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Session-Id': this.sessionGuard.getSessionId(),
      },
      body: JSON.stringify(body),
      signal,
    }).then(async (res) => {
      if (!res.ok || !res.body) {
        handlers['error']?.({ message: `HTTP ${res.status}` });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          let eventType = 'message';
          let dataLine = '';
          for (const line of part.split('\n')) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataLine = line.slice(6).trim();
          }
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine);
            const handler = handlers[eventType];
            if (handler) this.zone.run(() => handler(parsed));
          } catch { /* malformed SSE event — skip */ }
        }
      }
    }).catch((err) => {
      if (err.name !== 'AbortError') {
        handlers['error']?.({ message: err.message ?? 'Unknown error' });
      }
    });
  }
}
